<!--
SPDX-FileCopyrightText: 2025 Land Baden-Württemberg <InnoLab@stm.bwl.de>

SPDX-License-Identifier: MPL-2.0

@component
Lists all transcription jobs, eg. uploaded files

-->
<script>
import { onMount } from 'svelte';
import { getAddTranscriptionJobIdToArtifactsDefault, getApiUrl } from '../../config';
import {
  jobstatus_done,
  jobstatus_pending,
  jobstatus_transcribing,
  jobstatus_transcription_downloaded,
  jobstatus_translating,
} from '../../libs/TranscriptionAPI/TranscriptionJobs';
import { TranscriptionJobsStore } from '../../libs/TranscriptionAPI/TranscriptionJobsStore';
import { post } from '../../utils/CoreService';
import { getMicroserviceConfig } from '../../utils/MicroserviceConfigs';
import { snackbarStore } from '../../utils/SnackbarStore';
import uiStore from '../../utils/UIStore';
import DownloadFormatMenu from './DownloadFormatMenu.svelte';
import SingleTranscript from './SingleTranscript.svelte';

let transcriptDownloadFormats = [];
let translationMode = undefined;
let translationTargetLanguage = undefined;

onMount(async () => {
  const [translationModeResponse, translationTargetLanguageResponse, downloadFormatsResponse] = await Promise.all([
    getMicroserviceConfig('transcription', 'translation_mode'),
    getMicroserviceConfig('transcription', 'translation_target_language'),
    getMicroserviceConfig('transcription', 'download_formats'),
  ]);

  translationMode = translationModeResponse;
  translationTargetLanguage = translationTargetLanguageResponse;
  transcriptDownloadFormats = normalizeDownloadFormats(downloadFormatsResponse);
});

$: groupedJobs = {
  ok: $TranscriptionJobsStore.filter((j) => !j.hasError()),
  error: $TranscriptionJobsStore.filter((j) => j.hasError()),
  transcription_downloaded: $TranscriptionJobsStore.filter(
    (j) => j.getJobStatus() === jobstatus_transcription_downloaded,
  ),
};

function normalizeDownloadFormats(formats) {
  if (!Array.isArray(formats)) return [];

  return [
    ...new Set(
      formats
        .map((format) =>
          String(format || '')
            .trim()
            .toLowerCase()
            .replace(/^\.+/, ''),
        )
        .filter(Boolean),
    ),
  ];
}

function getTranscriptDownloadPayload(job, fileFormat) {
  return {
    job_id: job.getJobId(),
    file_format: fileFormat,
    add_job_id: getAddTranscriptionJobIdToArtifactsDefault(),
    speaker: Object.entries(job.getTranscription().getSpeakerMap()).map(([speaker, label]) => ({
      'speaker-name-ID': speaker,
      'speaker-name-edited': label.name,
    })),
  };
}

function downloadBlob(blob, fileName) {
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
}

/**
 * Function for downloading the transcript as a document.
 */
async function handleDownloadTranscript(jobId, fileFormat) {
  if (!jobId) return;
  let job = TranscriptionJobsStore.getJobById(jobId);
  if (!job) return;
  let transcription = job.getTranscription();
  if (!transcription) return;

  const payload = [getTranscriptDownloadPayload(job, fileFormat)];

  try {
    const url = `${getApiUrl()}/transcription/download`;
    const response = await post(url, payload, {
      contentType: 'application/json',
    });
    if (response.ok) {
      const blob = await response.blob();
      const fileName = job.getFileName();
      downloadBlob(blob, `Transkript_${fileName.split('.')[0]}.${fileFormat}`);
    } else {
      console.error('Download transcript request failed', {
        jobId,
        fileFormat,
        url,
        payload,
        status: response.status,
        statusText: response.statusText,
        responseBody: await response.clone().text(),
      });
      snackbarStore.update((current) => ({
        ...current,
        snackbarOpen: true,
        snackbarSeverity: 'error',
        snackbarMessage: 'Fehler beim Download des Transkripts.',
      }));
      console.error(`Failed to download the ${fileFormat} file for`, jobId);
    }
  } catch (error) {
    snackbarStore.update((current) => ({
      ...current,
      snackbarOpen: true,
      snackbarSeverity: 'error',
      snackbarMessage: 'Fehler beim Download des Transkripts.',
    }));
    console.error('Failed to download the docx file for', jobId, error);
  }
}

async function handleDownloadAllTranscripts(fileFormat) {
  const job_with_transcription = groupedJobs.transcription_downloaded;

  if (job_with_transcription.length === 0) {
    snackbarStore.update((current) => ({
      ...current,
      snackbarOpen: true,
      snackbarSeverity: 'error',
      snackbarMessage: 'Es sind keine Transkripte verfügbar',
    }));
    return;
  }

  const fileName = job_with_transcription.length === 1 ? `Transkript.${fileFormat}` : 'Transkripte.zip';

  const payload = job_with_transcription.map((job) => getTranscriptDownloadPayload(job, fileFormat));
  try {
    const url = `${getApiUrl()}/transcription/download`;
    const response = await post(url, payload, {
      contentType: 'application/json',
    });
    if (response.ok) {
      const blob = await response.blob();
      downloadBlob(blob, fileName);
    } else {
      console.error(`Failed to download ${fileName}`);
      snackbarStore.update((current) => ({
        ...current,
        snackbarOpen: true,
        snackbarSeverity: 'error',
        snackbarMessage: 'Fehler beim Download der Transkripte.',
      }));
    }
  } catch (error) {
    console.error(`Failed to download ${fileName}`, error);
    snackbarStore.update((current) => ({
      ...current,
      snackbarOpen: true,
      snackbarSeverity: 'error',
      snackbarMessage: 'Fehler beim Download der Transkripte.',
    }));
  }
}

function isNonEmptyString(value) {
  return typeof value === 'string' && value.trim() !== '';
}

function canRequestTranslation(job) {
  const transcription = job.getTranscription();
  const language = transcription?.getLanguage();
  const targetLanguage = translationTargetLanguage;

  return (
    translationMode === 'on_request' &&
    job.getJobStatus() === jobstatus_transcription_downloaded &&
    isNonEmptyString(language) &&
    isNonEmptyString(targetLanguage) &&
    !transcription.hasTranslation() &&
    language.toLowerCase() !== targetLanguage.toLowerCase()
  );
}

async function handleRequestTranslation(jobId, event) {
  const liElement = event.currentTarget.closest('li');
  liElement.classList.remove('show-transcript');

  const job = TranscriptionJobsStore.getJobById(jobId);
  const url = getApiUrl() + '/transcription/translate/' + encodeURIComponent(jobId);

  try {
    const response = await post(url);
    if (response.ok) {
      job.setStatus(jobstatus_translating);
      TranscriptionJobsStore.updateJob(job);
      return;
    }

    snackbarStore.update((current) => ({
      ...current,
      snackbarOpen: true,
      snackbarSeverity: 'error',
      snackbarMessage: uiTexts.transcription.requestTranslationError,
    }));
  } catch (error) {
    console.error('Failed to request translation for', jobId, error);
    snackbarStore.update((current) => ({
      ...current,
      snackbarOpen: true,
      snackbarSeverity: 'error',
      snackbarMessage: uiTexts.transcription.requestTranslationError,
    }));
  }
}

function deleteAllTranscriptions() {
  TranscriptionJobsStore.reset();
}

function deleteAllErrorTranscriptions() {
  TranscriptionJobsStore.removeFailedTranscriptionJobs();
}

//Transkription Toggle
function toggleTranscript(event) {
  const button = event.currentTarget;
  const liElement = button.closest('li');

  if (liElement) {
    liElement.classList.toggle('show-transcript');
  }
}

//load ui texts from ui Store
let uiTexts;
uiStore.textStore.subscribe((store) => {
  uiTexts = store.texts;
});
</script>

{#if groupedJobs.ok.length > 0}
  <h3 class="transcription-jobs-heading">
    {uiTexts.transcription.transcriptionsHeading}
  </h3>
    <div class="upload-list-wrapper" style="margin-top: 2rem; width: 100%; max-width: 1200px;">
      <ul class="upload-list">
        {#each groupedJobs.ok as job (job.getJobId())}
          <li class="transcript-job">
			<div style="display: flex; justify-content: space-between; gap: 0.5rem; width: 100%;">
				<span style="flex-grow: 1; padding-top:8px; overflow: hidden; text-overflow: ellipsis; font-weight:bold;text-align:left; white-space: nowrap;">{job.getFileName()}</span>
				{#if job.getJobStatus() != jobstatus_transcription_downloaded}
					<span style="flex: 1; color: #888; text-align: right;">
						{#if job.getJobStatus() == jobstatus_pending}
              {uiTexts.transcription.statusPending}
						{:else if job.getJobStatus() == jobstatus_transcribing}
              {uiTexts.transcription.statusTranscribing}
						{:else if job.getJobStatus() == jobstatus_translating}
              {uiTexts.transcription.statusTranslating}
						{:else if job.getJobStatus() == jobstatus_done}
              {uiTexts.transcription.statusDone}
						{/if}
					</span>
				{:else}
					<div style="display: flex; gap: 12px;">
					<button class="secondary" aria-label="{uiTexts.transcription.removeTranscript}"
                            on:click={() => {
                                TranscriptionJobsStore.removeTranscriptionJob(job.getJobId());
                            }}
					>
					<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="32" height="32" fill="currentColor" viewBox="0 0 256 256"><path d="M216,48H176V40a24,24,0,0,0-24-24H104A24,24,0,0,0,80,40v8H40a8,8,0,0,0,0,16h8V208a16,16,0,0,0,16,16H192a16,16,0,0,0,16-16V64h8a8,8,0,0,0,0-16ZM96,40a8,8,0,0,1,8-8h48a8,8,0,0,1,8,8v8H96Zm96,168H64V64H192ZM112,104v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Zm48,0v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Z"></path></svg>
					{uiTexts.transcription.removeTranscript}</button>
            {#if canRequestTranslation(job)}
              <button class="secondary" on:click={(event) => handleRequestTranslation(job.getJobId(), event)}>
                {uiTexts.transcription.requestTranslation}
              </button>
            {/if}
            <DownloadFormatMenu
              label={uiTexts.transcription.downloadPrefix}
              formats={transcriptDownloadFormats}
              onSelect={(downloadFormat) => handleDownloadTranscript(job.getJobId(), downloadFormat)}
            />
          <button on:click={toggleTranscript} class="secondary caret" aria-label="{uiTexts.transcription.showTranscript}">
              <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="32" height="32" fill="currentColor" viewBox="0 0 256 256"><path d="M213.66,101.66l-80,80a8,8,0,0,1-11.32,0l-80-80A8,8,0,0,1,53.66,90.34L128,164.69l74.34-74.35a8,8,0,0,1,11.32,11.32Z"></path></svg>
          </button>
					</div>
				{/if}
			</div>
              {#if job.getTranscription()}
                <SingleTranscript bind:transcript={job.transcription} {job} />
              {/if}
          </li>
        {/each}
      </ul>

<!-- Leiste für "Alle" -->
      <div class="batch-action-bar grey-bg" style="display: flex; gap: 12px;">
      <button class="secondary" on:click={() => {
      snackbarStore.update(current => ({
          ...current,
          snackbarOpen: true,
          snackbarSeverity: "confirmation",
          snackbarMessage: "Möchten Sie wirklich alle Transkripte löschen?",
          snackbarAction: deleteAllTranscriptions
      }));
  }}> {uiTexts.transcription.removeAllTranscripts} {#if groupedJobs.error.length > 0}{uiTexts.transcription.withFailed} {/if}</button>
      {#if groupedJobs.transcription_downloaded.length > 0}
        <DownloadFormatMenu
          label={uiTexts.transcription.downloadAllPrefix}
          formats={transcriptDownloadFormats}
          onSelect={handleDownloadAllTranscripts}
        />
      {/if}
      </div>

    </div>
{/if}


{#if groupedJobs.error.length > 0}
    <div class="upload-list-wrapper" style="margin-top: 4rem; width: 100%; max-width: 1200px;">
		<h2 class="headline-errors">{uiTexts.transcription.transcriptionsWithErrors}</h2>
      <ul class="upload-list">
        {#each groupedJobs.error as job (job.getJobId())}
          <li class="transcript-job">
			<div style="display: flex; justify-content: space-between; gap: 0.5rem; width: 100%;">
				<span style="flex-grow: 1; padding-top:8px; overflow: hidden; text-overflow: ellipsis; font-weight:bold;text-align:left; white-space: nowrap;">{job.file_name}</span>
				<span style="flex: 1; color: #888; text-align: right;">
					{job.getErrorMessage()}
				</span>
			</div>
          </li>
        {/each}
      </ul>

<!-- Leiste für "Alle" -->
	  <div class="batch-action-bar grey-bg" style="display: flex; gap: 12px;">
		<button class="secondary" on:click={deleteAllErrorTranscriptions}> {uiTexts.transcription.deleteAllWithErrors}</button>
	  </div>

    </div>
{/if}


<style>
  .transcription-jobs-heading {
      width: 100%;
      max-width: 640px;
      margin: 1rem auto 1rem;
      padding-top: 1rem;
      border-top: 2px solid #ddd;
      font-size: 1rem;
      font-weight: 600;
      text-align: center;
    }
    li.transcript-job{
        padding: 8px;
        width: 100%;
        border: 1px solid #e2e2e2;
        display:block;
        margin-bottom: 8px;
    }
		.headline-errors {
			font-size: 1.3em;
			color: #8e0e0e;
			text-transform: uppercase;
		}
</style>
